import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  Image,
  Video,
  FileText,
  Wand2,
  Send,
  CheckCircle,
  Loader2,
  Play,
  Volume2,
  Mic,
  X,
  Plus,
  Zap,
  Eye,
  Download,
  Share2,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Wizard Steps
const STEPS = [
  { id: 1, name: "Choose Type", description: "What do you want to create?" },
  { id: 2, name: "Add Content", description: "Upload or enter your content" },
  { id: 3, name: "Generate Script", description: "AI writes your script" },
  { id: 4, name: "Create Media", description: "Generate images & voice" },
  { id: 5, name: "Preview & Publish", description: "Review and broadcast" }
];

// Content Types
const contentTypes = [
  { id: "text-to-video", label: "Text to Video", icon: FileText, description: "Enter text, we create a video", color: "from-primary to-purple-600" },
  { id: "photo-to-video", label: "Photo to Video", icon: Image, description: "Upload photos, turn into video", color: "from-accent-neon to-blue-600" },
  { id: "video-edit", label: "Edit Video", icon: Video, description: "Upload and enhance a video", color: "from-red-500 to-orange-600" }
];

// Video Types
const videoTypes = [
  { value: "sales", label: "Sales", icon: "💰" },
  { value: "educational", label: "Educational", icon: "📚" },
  { value: "marketing", label: "Marketing", icon: "📢" },
  { value: "explainer", label: "Explainer", icon: "💡" }
];

// Voice Options
const voiceOptions = [
  { id: "alloy", name: "Alloy", description: "Neutral & balanced" },
  { id: "echo", name: "Echo", description: "Warm & friendly" },
  { id: "fable", name: "Fable", description: "Expressive & dramatic" },
  { id: "onyx", name: "Onyx", description: "Deep & authoritative" },
  { id: "nova", name: "Nova", description: "Energetic & youthful" },
  { id: "shimmer", name: "Shimmer", description: "Clear & professional" }
];

export default function VideoWizard() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Step 1: Content Type
  const [contentType, setContentType] = useState(null);
  const [videoType, setVideoType] = useState("sales");
  
  // Step 2: Content
  const [textContent, setTextContent] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [productName, setProductName] = useState("");
  
  // Step 3: Script
  const [generatedScript, setGeneratedScript] = useState("");
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  
  // Step 4: Media
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [generatedImages, setGeneratedImages] = useState([]);
  const [generatedVoice, setGeneratedVoice] = useState(null);
  const [isGeneratingMedia, setIsGeneratingMedia] = useState(false);
  
  // Step 5: Preview
  const [projectId, setProjectId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Handle file upload
  const handleFileUpload = (files) => {
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedFiles(prev => [...prev, {
          id: Date.now() + Math.random(),
          file,
          preview: e.target?.result,
          name: file.name,
          type: file.type.startsWith('image/') ? 'image' : 'video'
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (id) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
  };

  // Step Navigation
  const canProceed = () => {
    switch (currentStep) {
      case 1: return contentType !== null;
      case 2: 
        if (contentType === 'text-to-video') return textContent.trim().length > 10;
        if (contentType === 'photo-to-video') return uploadedFiles.length > 0 && productName.trim();
        return uploadedFiles.length > 0;
      case 3: return generatedScript.trim().length > 0;
      case 4: return generatedImages.length > 0 || generatedVoice;
      case 5: return true;
      default: return false;
    }
  };

  const nextStep = () => {
    if (currentStep < 5 && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Generate Script with AI
  const generateScript = async () => {
    setIsGeneratingScript(true);
    setProgress(0);
    
    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      let prompt = "";
      if (contentType === 'text-to-video') {
        prompt = textContent;
      } else if (contentType === 'photo-to-video') {
        prompt = `Product: ${productName}. Create a compelling ${videoType} video script.`;
      }

      const response = await axios.post(`${API}/videos/generate-script`, {
        video_type: videoType,
        topic: prompt,
        target_duration: 60,
        tone: "professional"
      }, { headers: getAuthHeaders() });

      clearInterval(progressInterval);
      setProgress(100);
      
      setGeneratedScript(response.data.script);
      toast.success("Script generated!");
    } catch (error) {
      console.error("Script generation error:", error);
      // Fallback script for demo
      const fallbackScript = `Welcome to ${productName || 'our product'}!

This ${videoType} video showcases the amazing features that make us stand out.

Key highlights:
• Premium quality and design
• Exceptional value for your investment
• Trusted by thousands of satisfied customers

${textContent ? textContent.slice(0, 200) : 'Experience the difference today.'}

Contact us now to learn more and take the next step!`;
      
      setGeneratedScript(fallbackScript);
      toast.success("Script created!");
    } finally {
      setIsGeneratingScript(false);
      setProgress(0);
    }
  };

  // Generate Media (Images + Voice)
  const generateMedia = async () => {
    setIsGeneratingMedia(true);
    setProgress(0);
    
    try {
      // Generate images
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 90));
      }, 1000);

      // If we have uploaded images, use those
      if (uploadedFiles.length > 0) {
        setGeneratedImages(uploadedFiles.map(f => ({
          id: f.id,
          url: f.preview,
          type: 'uploaded'
        })));
      } else {
        // Generate images with AI
        try {
          const imagePrompt = `Professional ${videoType} image for: ${productName || generatedScript.slice(0, 100)}`;
          const imgResponse = await axios.post(`${API}/images/generate`, {
            prompt: imagePrompt,
            style: "professional, high quality, marketing",
            provider: "openai"
          }, { headers: getAuthHeaders() });

          if (imgResponse.data.url) {
            setGeneratedImages([{
              id: Date.now(),
              url: `${process.env.REACT_APP_BACKEND_URL}${imgResponse.data.url}`,
              type: 'generated'
            }]);
          }
        } catch (imgError) {
          console.log("Image generation fallback");
          // Use placeholder
          setGeneratedImages([{
            id: Date.now(),
            url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800',
            type: 'placeholder'
          }]);
        }
      }

      // Generate voice
      try {
        const voiceResponse = await axios.post(`${API}/voice/generate`, {
          text: generatedScript.slice(0, 500),
          voice: selectedVoice,
          provider: "openai",
          model: "tts-1"
        }, { headers: getAuthHeaders() });

        if (voiceResponse.data.url) {
          setGeneratedVoice({
            url: `${process.env.REACT_APP_BACKEND_URL}${voiceResponse.data.url}`,
            id: voiceResponse.data.id
          });
        }
      } catch (voiceError) {
        console.log("Voice generation skipped");
      }

      clearInterval(progressInterval);
      setProgress(100);
      toast.success("Media generated!");
    } catch (error) {
      console.error("Media generation error:", error);
      toast.error("Some media generation failed, but you can continue");
    } finally {
      setIsGeneratingMedia(false);
      setProgress(0);
    }
  };

  // Create Final Video
  const createVideo = async () => {
    setIsGeneratingVideo(true);
    setProgress(0);
    
    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 2, 95));
      }, 2000);

      // Create project first
      const projectResponse = await axios.post(`${API}/projects`, {
        name: productName || "New Video Project",
        video_type: videoType,
        topic: textContent || productName,
        script: generatedScript,
        target_duration: 60,
        tone: "professional"
      }, { headers: getAuthHeaders() });

      const newProjectId = projectResponse.data.id;
      setProjectId(newProjectId);

      // Generate video
      const videoResponse = await axios.post(`${API}/videos/generate/${newProjectId}`, {
        prompt: generatedScript.slice(0, 300),
        duration: 8,
        size: "1280x720"
      }, { headers: getAuthHeaders() });

      clearInterval(progressInterval);
      setProgress(100);

      if (videoResponse.data.url) {
        setVideoUrl(`${process.env.REACT_APP_BACKEND_URL}${videoResponse.data.url}`);
        toast.success("Video created successfully!");
      } else {
        toast.success("Video is being processed. Check your library shortly.");
      }
    } catch (error) {
      console.error("Video creation error:", error);
      toast.error("Video creation in progress - check library");
    } finally {
      setIsGeneratingVideo(false);
      setProgress(0);
    }
  };

  // Auto-generate when entering step 3
  useEffect(() => {
    if (currentStep === 3 && !generatedScript && !isGeneratingScript) {
      generateScript();
    }
  }, [currentStep]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              onClick={() => currentStep === 1 ? navigate(-1) : prevStep()}
              className="text-text-secondary hover:text-text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentStep === 1 ? 'Back' : 'Previous'}
            </Button>
            <span className="font-heading font-bold text-text-primary">Video Wizard</span>
            <span className="text-sm text-text-muted">Step {currentStep} of 5</span>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all ${
                  currentStep > step.id 
                    ? 'bg-accent-success text-white' 
                    : currentStep === step.id 
                      ? 'bg-primary text-white' 
                      : 'bg-background-subtle text-text-muted'
                }`}>
                  {currentStep > step.id ? <CheckCircle className="w-5 h-5" /> : step.id}
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-12 md:w-20 h-1 mx-1 rounded ${
                    currentStep > step.id ? 'bg-accent-success' : 'bg-background-subtle'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Choose Type */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                  What do you want to create?
                </h2>
                <p className="text-text-secondary">Choose how you'd like to make your video</p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {contentTypes.map(type => {
                  const IconComponent = type.icon;
                  const isSelected = contentType === type.id;
                  return (
                    <Card
                      key={type.id}
                      onClick={() => setContentType(type.id)}
                      className={`cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-2 border-primary bg-primary/10 shadow-lg' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <CardContent className="p-6 text-center">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${type.color} flex items-center justify-center text-white mx-auto mb-4`}>
                          <IconComponent className="w-8 h-8" />
                        </div>
                        <h3 className="font-bold text-lg text-text-primary mb-2">{type.label}</h3>
                        <p className="text-sm text-text-muted">{type.description}</p>
                        {isSelected && <CheckCircle className="w-6 h-6 text-primary mx-auto mt-4" />}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {contentType && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6"
                >
                  <Label className="text-text-primary mb-3 block">Video Style</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {videoTypes.map(type => (
                      <button
                        key={type.value}
                        onClick={() => setVideoType(type.value)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          videoType === type.value
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <span className="text-2xl block mb-1">{type.icon}</span>
                        <span className="text-sm text-text-secondary">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Step 2: Add Content */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                  {contentType === 'text-to-video' ? 'Enter Your Content' : 'Upload Your Media'}
                </h2>
                <p className="text-text-secondary">
                  {contentType === 'text-to-video' 
                    ? 'Tell us about your product or message' 
                    : 'Add photos or videos to transform'}
                </p>
              </div>

              {contentType === 'text-to-video' ? (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <Label className="text-text-primary mb-2 block">
                      What's your video about?
                    </Label>
                    <Textarea
                      value={textContent}
                      onChange={(e) => setTextContent(e.target.value)}
                      placeholder="Describe your product, service, or message. The more detail you provide, the better your video will be.

Example: We're launching a new luxury watch collection. The watches feature Swiss movement, sapphire crystal, and genuine leather straps. Target audience is professionals aged 30-50 who appreciate quality craftsmanship."
                      className="bg-background border-border text-text-primary min-h-[200px]"
                    />
                    <p className="text-xs text-text-muted mt-2">
                      {textContent.length} characters • Minimum 10 recommended
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="bg-background-paper border-border">
                    <CardContent className="p-6">
                      <Label className="text-text-primary mb-2 block">Product/Service Name</Label>
                      <Input
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        placeholder="e.g., Luxury Watch Collection, Fitness App, Consulting Service"
                        className="bg-background border-border"
                      />
                    </CardContent>
                  </Card>

                  <Card className="bg-background-paper border-border">
                    <CardContent className="p-6">
                      <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        accept="image/*,video/*"
                        onChange={(e) => handleFileUpload(e.target.files)}
                        className="hidden"
                      />
                      
                      {uploadedFiles.length === 0 ? (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-all"
                        >
                          <Upload className="w-12 h-12 text-text-muted mx-auto mb-3" />
                          <p className="text-text-primary font-medium mb-1">Upload Photos or Videos</p>
                          <p className="text-text-muted text-sm">Click or drag files here</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                          {uploadedFiles.map((file, index) => (
                            <div key={file.id} className="relative group aspect-square">
                              <div className="w-full h-full rounded-lg overflow-hidden bg-background-subtle">
                                {file.type === 'image' ? (
                                  <img src={file.preview} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <video src={file.preview} className="w-full h-full object-cover" />
                                )}
                              </div>
                              <button
                                onClick={() => removeFile(file.id)}
                                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <div className="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                                {index + 1}
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center"
                          >
                            <Plus className="w-6 h-6 text-text-muted" />
                          </button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </motion.div>
          )}

          {/* Step 3: Generate Script */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                  Your Video Script
                </h2>
                <p className="text-text-secondary">AI-generated script based on your content</p>
              </div>

              {isGeneratingScript ? (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-8 text-center">
                    <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                    <p className="text-text-primary font-medium mb-2">Generating your script...</p>
                    <Progress value={progress} className="w-full max-w-xs mx-auto" />
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-text-primary font-medium">Video Script</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={generateScript}
                        className="text-primary"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Regenerate
                      </Button>
                    </div>
                    <Textarea
                      value={generatedScript}
                      onChange={(e) => setGeneratedScript(e.target.value)}
                      className="bg-background border-border text-text-primary min-h-[300px]"
                      placeholder="Your script will appear here..."
                    />
                    <p className="text-xs text-text-muted mt-2">
                      Edit the script to customize your message
                    </p>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Step 4: Create Media */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                  Generate Media Assets
                </h2>
                <p className="text-text-secondary">Create images and voice narration for your video</p>
              </div>

              {/* Voice Selection */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6">
                  <Label className="text-text-primary font-medium mb-3 block">
                    <Mic className="w-4 h-4 inline mr-2" />
                    Choose Voice
                  </Label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {voiceOptions.map(voice => (
                      <button
                        key={voice.id}
                        onClick={() => setSelectedVoice(voice.id)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          selectedVoice === voice.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Volume2 className={`w-5 h-5 mx-auto mb-1 ${selectedVoice === voice.id ? 'text-primary' : 'text-text-muted'}`} />
                        <span className="text-sm font-medium text-text-primary block">{voice.name}</span>
                        <span className="text-xs text-text-muted">{voice.description}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Generate Button */}
              {!isGeneratingMedia && generatedImages.length === 0 && (
                <Button
                  onClick={generateMedia}
                  className="w-full bg-primary hover:bg-primary-hover text-white py-6"
                >
                  <Wand2 className="w-5 h-5 mr-2" />
                  Generate Images & Voice
                </Button>
              )}

              {/* Progress */}
              {isGeneratingMedia && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-8 text-center">
                    <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                    <p className="text-text-primary font-medium mb-2">Generating media...</p>
                    <Progress value={progress} className="w-full max-w-xs mx-auto" />
                  </CardContent>
                </Card>
              )}

              {/* Generated Media Preview */}
              {generatedImages.length > 0 && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <Label className="text-text-primary font-medium mb-3 block">
                      <Image className="w-4 h-4 inline mr-2" />
                      Generated Images
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {generatedImages.map(img => (
                        <div key={img.id} className="aspect-video rounded-lg overflow-hidden bg-background-subtle">
                          <img src={img.url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {generatedVoice && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <Label className="text-text-primary font-medium mb-3 block">
                      <Volume2 className="w-4 h-4 inline mr-2" />
                      Voice Narration
                    </Label>
                    <audio controls className="w-full">
                      <source src={generatedVoice.url} type="audio/mpeg" />
                    </audio>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Step 5: Preview & Publish */}
          {currentStep === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                  Preview & Publish
                </h2>
                <p className="text-text-secondary">Review your video and share it with the world</p>
              </div>

              {!videoUrl && !isGeneratingVideo && (
                <Button
                  onClick={createVideo}
                  className="w-full bg-accent-warning hover:bg-accent-warning/90 text-black py-6 font-bold"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Create Final Video
                </Button>
              )}

              {isGeneratingVideo && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-8 text-center">
                    <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                    <p className="text-text-primary font-medium mb-2">Creating your video...</p>
                    <p className="text-text-muted text-sm mb-4">This may take a few minutes</p>
                    <Progress value={progress} className="w-full max-w-xs mx-auto" />
                  </CardContent>
                </Card>
              )}

              {videoUrl && (
                <>
                  <Card className="bg-background-paper border-border overflow-hidden">
                    <div className="aspect-video bg-black">
                      <video
                        src={videoUrl}
                        controls
                        className="w-full h-full"
                        poster={generatedImages[0]?.url}
                      />
                    </div>
                  </Card>

                  <div className="grid grid-cols-3 gap-3">
                    <Button
                      variant="outline"
                      className="py-6"
                      onClick={() => window.open(videoUrl, '_blank')}
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download
                    </Button>
                    <Button
                      className="py-6 bg-accent-success hover:bg-accent-success/90"
                      onClick={() => navigate(`/broadcast/${projectId}`)}
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Broadcast
                    </Button>
                    <Button
                      variant="outline"
                      className="py-6"
                      onClick={() => navigate('/library')}
                    >
                      <Eye className="w-5 h-5 mr-2" />
                      View Library
                    </Button>
                  </div>
                </>
              )}

              {!videoUrl && !isGeneratingVideo && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-text-primary mb-4">Summary</h3>
                    <div className="space-y-2 text-sm">
                      <p className="text-text-secondary">
                        <span className="text-text-muted">Type:</span> {contentType}
                      </p>
                      <p className="text-text-secondary">
                        <span className="text-text-muted">Style:</span> {videoType}
                      </p>
                      <p className="text-text-secondary">
                        <span className="text-text-muted">Images:</span> {generatedImages.length}
                      </p>
                      <p className="text-text-secondary">
                        <span className="text-text-muted">Voice:</span> {selectedVoice}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-border">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < 5 ? (
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
              className="bg-primary hover:bg-primary-hover"
            >
              Next Step
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/library')}
              className="bg-accent-success hover:bg-accent-success/90"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Finish
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
